import { api as rawApi } from "../../shared/api/client";
import { isTemp, realPath, realTaskId } from "./lib/tempIds";
import { readVideoDuration, uploadToSignedUrl } from "../../shared/api/tasks.api";
import { DEPT_NAME, rememberPeople, setDirectory, type DirectoryInput } from "./data/people";
import { PLACEHOLDER_POSTER } from "./lib/poster";
import { dayOfDate, toMin } from "./lib/time";
import type { Busy, Delivery, FileItem, FileKind, FrameDrawing, HistoryKind, ItemStatus, Status, SubTask, Task, Topic } from "./types";

/**
 * Тот же клиент, но с настоящими id: запрос по новой публикации, которая ещё
 * в окне «Отменить», сперва досылает её создание (см. lib/tempIds).
 */
type Opts = Parameters<typeof rawApi.get>[1];
const withTaskId = async (data: any) => (data && isTemp(data.taskId) ? { ...data, taskId: await realTaskId(data.taskId) } : data);
const api = {
  get: async <T,>(path: string, options?: Opts) => rawApi.get<T>(await realPath(path), options),
  post: async <T,>(path: string, data?: any, options?: Opts) => rawApi.post<T>(await realPath(path), await withTaskId(data), options),
  patch: async <T,>(path: string, data?: any, options?: Opts) => rawApi.patch<T>(await realPath(path), data, options),
  delete: async <T,>(path: string, options?: Opts) => rawApi.delete<T>(await realPath(path), options),
};

/**
 * Сервер раздела: `/montage/*` в edge-функции. Здесь же перевод серверной
 * задачи в тип прототипа — даты в «минуты» и номера дней, пункты, версии со
 * ссылками на видео.
 */

interface ApiFile { id: string; name: string; size: number; kind: FileKind; duration?: string }

interface ApiVersion {
  id: string; n: number; fileName: string; size: number; duration: number; comment?: string;
  createdAt: string; videoUrl: string | null; posterUrl: string | null;
  images?: { url: string | null; name: string }[] | null;
  moderation?: { verdict: "ok" | "rework"; byId: string; at: string } | null;
  delivery?: Delivery;
  telegramUrl?: string | null;
}

interface ApiItem {
  id: string; title: string; format: string; brief: string; reference?: string;
  status: ItemStatus; round: number;
  moderation?: { verdict: "ok" | "rework"; byId: string; at: string } | null;
  approvedById: string | null; approvedAt: string | null;
  autoPublishInstagram?: boolean;
  autoPublishState?: "off" | "waiting" | "publishing" | "published" | "failed";
  autoPublishError?: string | null;
  link: string | null; publishedById: string | null; publishedAt: string | null;
  autoLinked?: boolean;
  files: ApiFile[];
  versions: ApiVersion[];
  revisions: { id: string; round: number; tc: number; text: string; resolved: boolean; byId?: string | null; drawing?: FrameDrawing | null }[];
}

export interface ApiTask {
  id: string;
  code: string;
  title: string;
  divisionId: string;
  projectId: string | null;
  topicId: string | null;
  topic: { title: string; period: "day" | "week" } | null;
  creatorId: string;
  editorId: string | null;
  groupId?: string | null;
  period?: "day" | "week";
  description?: string;
  status: Status;
  workDate: string;
  dueAt: string;
  estimate: number;
  createdAt: string;
  acceptedAt: string | null;
  approvedAt: string | null;
  justification: string | null;
  justifiedAt: string | null;
  proposal: { date: string; dueAt: string; note: string; at: string } | null;
  items: ApiItem[];
  messages: { id: string; authorId: string; text: string; createdAt: string }[];
  events: { kind: HistoryKind; text: string; createdAt: string }[];
}

/** Строка раздела «Видео»: кто сделал, в какой теме, и что с постом. */
/** Пост из ленты аккаунта, который можно приложить к принятому ролику. */
export interface PostCandidate {
  mediaId: string;
  link: string;
  thumbnail: string | null;
  publishedAt: string;
  account: string;
  accountId: string;
  platform: "Instagram" | "TikTok";
  mediaType: string | null;
  views: number | null;
  /** Совпадение текста обложки с названием ролика, 0…1; null — не сверяли. */
  match?: number | null;
}

/** Пост аккаунта для «Посты» монтажёра: к какому ролику он приложен, если приложен. */
export interface AccountPost {
  mediaId: string;
  link: string;
  thumbnail: string | null;
  publishedAt: string;
  mediaType: string | null;
  views: number | null;
  /** Текст обложки (сверка) — черновик названия, когда пост присваивают без темы. */
  cover: string | null;
  linked: { itemId: string; taskId: string; title: string; editor: string | null; mine: boolean; auto: boolean } | null;
}

/** Ролик без ссылки, к которому человек может приложить пост — с любого шага и аккаунта. */
export interface WaitingItem {
  taskId: string;
  itemId: string;
  title: string;
  status: ItemStatus;
  accountId: string | null;
  /** Ролик ставили в этот аккаунт (или в направление этого аккаунта без выбора паблика). */
  here: boolean;
  approvedAt: string | null;
  createdAt: string | null;
  editor: string | null;
  mine: boolean;
  /** Подсказки сверки обложек: mediaId → совпадение 0…1. */
  match: Record<string, number>;
  /** Ключи постов, отвязанных от этого ролика («Не тот пост»). */
  rejected: string[];
}

export interface VideoRow {
  id: string;
  title: string;
  format: string;
  status: ItemStatus;
  link: string | null;
  publishedAt: string | null;
  submittedAt: string;
  versions: number;
  topic: { id: string; code: string; title: string; period: "day" | "week"; workDate: string };
  project: { id: string; name: string } | null;
  account: { id: string; name: string } | null;
  editor: { id: string; name: string };
  group: { id: string; name: string; contentMaker: string | null } | null;
  /** последний снимок поста; `null` — нет ссылки или пост ещё не в снимках */
  stats: { date: string; views: number | null; reach: number | null; likes: number | null; comments: number | null; shares: number | null; saved: number | null } | null;
  /** кто взял ролик в другой отдел; видно только руководителю отдела */
  takes: VideoTake[];
}

export interface VideoTake { by: string; division: string; at: string; public: string | null; link: string | null }

/** Запись «Отработано»: материал, который человек обработал, со ссылками и их просмотрами. */
export interface WorkEntry {
  id: string;
  user: { id: string; name: string };
  mine: boolean;
  title: string;
  /** false — «без ТЗ»: сделано по своей инициативе */
  withBrief: boolean;
  brief: string;
  workedOn: string;
  /** запись завела отметка «Взяла» в «Роликах B2G» */
  fromTake: boolean;
  links: { url: string; views: number | null }[];
}

export interface WorkInput { title: string; withBrief: boolean; brief: string; links: string[]; workedOn: string }

/** Вышедший ролик другого отдела — строка экрана «Ролики B2G». */
export interface TakeRow {
  id: string;
  title: string;
  format: string;
  link: string | null;
  publishedAt: string;
  division: { id: string; name: string };
  topic: { code: string; title: string };
  project: string | null;
  group: { name: string; hasChat: boolean } | null;
  editor: string | null;
  /** `available: false` — файл убран с сайта, он остался в Telegram-канале группы */
  file: { name: string; size: number; available: boolean; telegramUrl: string | null } | null;
  posterUrl: string | null;
  takes: { id: string; mine: boolean; by: string; at: string; public: string | null; link: string | null }[];
}

interface ApiBusy { id: string; editorId: string; date: string; hours: number; title: string; kind: "busy" | "meet" }

const file = (f: ApiFile): FileItem => ({ id: f.id, name: f.name, size: f.size, kind: f.kind, duration: f.duration, remote: true });

export function toTask(t: ApiTask): Task {
  const items: SubTask[] = t.items.map((i) => {
    const versions = i.versions.map((v) => ({
      id: v.id,
      n: v.n,
      at: toMin(v.createdAt),
      duration: v.duration,
      fileName: v.fileName,
      size: v.size,
      comment: v.comment,
      poster: v.posterUrl ?? undefined,
      videoUrl: v.videoUrl ?? undefined,
      images: v.images ?? null,
      mod: v.moderation ? { verdict: v.moderation.verdict, by: v.moderation.byId, at: toMin(v.moderation.at) } : undefined,
      delivery: v.delivery,
      telegramUrl: v.telegramUrl ?? null,
    }));
    return {
      id: i.id,
      title: i.title,
      format: i.format,
      brief: i.brief,
      reference: i.reference,
      accountId: (i as { accountId?: string | null }).accountId ?? null,
      autoPublishInstagram: Boolean(i.autoPublishInstagram),
      autoPublishState: i.autoPublishState ?? "off",
      autoPublishError: i.autoPublishError ?? undefined,
      files: i.files.map(file),
      status: i.status,
      round: i.round,
      versions,
      revisions: i.revisions.map((r) => ({ id: r.id, tc: r.tc, text: r.text, round: r.round, resolved: r.resolved, by: r.byId ?? undefined, drawing: r.drawing ?? undefined })),
      mod: i.moderation ? { verdict: i.moderation.verdict, by: i.moderation.byId, at: toMin(i.moderation.at) } : undefined,
      approvedBy: i.approvedById ?? undefined,
      approvedAt: i.approvedAt ? toMin(i.approvedAt) : undefined,
      link: i.link ?? undefined,
      publishedBy: i.publishedById ?? undefined,
      publishedAt: i.publishedAt ? toMin(i.publishedAt) : undefined,
      autoLinked: Boolean(i.autoLinked),
      poster: [...versions].reverse().find((v) => v.poster)?.poster ?? PLACEHOLDER_POSTER,
    };
  });
  return {
    id: t.id,
    divisionId: t.divisionId,
    projectId: t.projectId ?? null,
    topicId: t.topicId ?? null,
    topic: t.topic ?? null,
    code: t.code,
    title: t.title,
    description: t.description ?? "",
    period: t.period ?? "day",
    groupId: t.groupId ?? null,
    dept: DEPT_NAME[t.divisionId] ?? "",
    creator: t.creatorId,
    editor: t.editorId ?? "",
    status: t.status,
    items,
    day: dayOfDate(t.workDate),
    estimate: t.estimate,
    due: toMin(t.dueAt),
    createdAt: toMin(t.createdAt),
    acceptedAt: t.acceptedAt ? toMin(t.acceptedAt) : undefined,
    approvedAt: t.approvedAt ? toMin(t.approvedAt) : undefined,
    justification: t.justification ?? undefined,
    justifiedAt: t.justifiedAt ? toMin(t.justifiedAt) : undefined,
    chat: t.messages.map((m) => ({ id: m.id, from: m.authorId, text: m.text, at: toMin(m.createdAt) })),
    history: t.events.map((e) => ({ at: toMin(e.createdAt), kind: e.kind, text: e.text })),
    proposal: t.proposal
      ? { day: dayOfDate(t.proposal.date), due: toMin(t.proposal.dueAt), note: t.proposal.note, at: toMin(t.proposal.at) }
      : undefined,
    poster: [...items].reverse().find((i) => i.versions.length)?.poster ?? PLACEHOLDER_POSTER,
  };
}

const toBusy = (b: ApiBusy): Busy => ({ id: b.id, editor: b.editorId, day: dayOfDate(b.date), hours: b.hours, title: b.title, kind: b.kind });

type One = Promise<{ task: ApiTask }>;

export const montageApi = {
  async context() {
    const ctx = await api.get<DirectoryInput>("/montage/context");
    setDirectory(ctx);
    return ctx;
  },

  async tasks(from: string, to: string) {
    const res = await api.get<{ tasks: ApiTask[]; busy: ApiBusy[]; people: { id: string; name: string; avatarUrl: string | null }[] }>(
      "/montage/tasks",
      { params: { from, to } },
    );
    rememberPeople(res.people ?? []);
    return { tasks: res.tasks.map(toTask), busy: res.busy.map(toBusy) };
  },

  topics: (from: string, to?: string) =>
    api.get<{ topics: Topic[] }>("/montage/topics", { params: to ? { from, to } : { from } }),
  createTopic: (body: {
    divisionId: string; projectId: string; groupId?: string | null;
    period: "day" | "week"; startsOn: string; title: string; description?: string;
  }) => api.post<{ topic: Topic }>("/montage/topics", body),
  patchTopic: (id: string, body: { title?: string; description?: string; archived?: boolean }) =>
    api.patch<{ topic: Topic }>(`/montage/topics/${id}`, body),

  create: (body: {
    divisionId: string;
    projectId: string;
    topicId?: string | null;
    /** пусто — свободная тема; монтажёр ставит только себя, сервер это проверит */
    editorId: string | null;
    title: string;
    description?: string;
    period: "day" | "week";
    /** адресат свободной темы; руководитель может оставить пустым — всем группам */
    groupId?: string | null;
    workDate: string;
    dueAt: string;
    estimate: number;
    items: { title: string; format: string; brief: string; reference?: string; accountId?: string | null; autoPublishInstagram?: boolean; files: { path: string; name: string; size: number; kind: FileKind; duration?: string }[] }[];
  }, opts?: RequestInit): One => api.post("/montage/tasks", body, opts),

  accept: (id: string, opts?: RequestInit): One => api.post(`/montage/tasks/${id}/accept`, {}, opts),
  /** монтажёр берёт свободную тему себе */
  take: (id: string, opts?: RequestInit): One => api.post(`/montage/tasks/${id}/take`, {}, opts),
  /** постановщик назначает тему; `null` — вернуть в свободные */
  assign: (id: string, editorId: string | null, opts?: RequestInit): One =>
    api.post(`/montage/tasks/${id}/assign`, { editorId }, opts),
  /** видео в тему — монтажёр по ходу работы или постановщик заранее */
  addItem: (id: string, body: { title: string; format: string; brief?: string }) =>
    api.post<{ task: ApiTask; itemId: string }>(`/montage/tasks/${id}/items`, body),
  /** видео, сделанные людьми, с цифрами вышедших постов */
  /** `by: "published"` — окно по дате выхода (итог месяца), иначе — по первой сдаче */
  videos: (from: string, to?: string, by?: "published") =>
    api.get<{ videos: VideoRow[]; takesVisible?: boolean; statsError?: string | null }>("/montage/videos", { params: { from, ...(to ? { to } : {}), ...(by ? { by } : {}) } }),
  /** вышедшие ролики отделов, у которых мой отдел берёт */
  takesFeed: (from: string) =>
    api.get<{ sources: { id: string; name: string }[]; videos: TakeRow[]; today: number }>("/montage/takes/feed", { params: { from } }),
  /** «Взяла» — паблик и ссылка необязательны, повтор уточняет их */
  takeItem: (itemId: string, body: { public?: string; link?: string }) =>
    api.post<{ ok: true }>(`/montage/takes/items/${itemId}`, body),
  /** «Отработано» за период: свои записи, руководителю — и отделов, что берут у него */
  work: (from: string, to: string) => api.get<{ entries: WorkEntry[]; canAdd: boolean }>("/montage/work", { params: { from, to } }),
  addWork: (body: WorkInput) => api.post<{ id: string }>("/montage/work", body),
  editWork: (id: string, body: WorkInput) => api.patch<{ ok: true }>(`/montage/work/${id}`, body),
  removeWork: (id: string) => api.delete<{ ok: true }>(`/montage/work/${id}`),
  untakeItem: (itemId: string) => api.delete<{ ok: true }>(`/montage/takes/items/${itemId}`),
  takeDownload: (itemId: string) => api.get<{ url: string }>(`/montage/takes/items/${itemId}/download`),
  takeSource: (itemId: string) =>
    api.get<{ url: string; playUrl: string; fileName: string; title: string; images: { url: string | null; name: string }[] | null }>(`/montage/takes/items/${itemId}/source`),
  /** ссылка на скачивание оригинала версии (без v — последней) */
  // Не `download`: так уже называется скачивание файлов ниже, и второе имя
  // молча затирало первое — «Скачать» у ролика падал (09.10.2026).
  downloadItem: (id: string, itemId: string, v?: number) =>
    api.get<{ url: string }>(`/montage/tasks/${id}/items/${itemId}/download${v ? `?v=${v}` : ""}`),
  /** оригинал одобренного ролика и аккаунты направления — для «Опубликовать» */
  publishSource: (id: string, itemId: string) =>
    api.get<{ url: string; fileName: string; size: number; title: string; accountIds: string[] }>(`/montage/tasks/${id}/items/${itemId}/source`),
  /** выложен с сайта: сервер сам берёт у Instagram ссылку и закрывает ролик */
  publishedMedia: (id: string, itemId: string, body: { accountId: string; mediaId: string }) =>
    api.post<{ link: string }>(`/montage/tasks/${id}/items/${itemId}/published-media`, body),
  propose: (id: string, body: { workDate: string; dueAt: string; note: string }, opts?: RequestInit): One =>
    api.post(`/montage/tasks/${id}/propose`, body, opts),
  acceptProposal: (id: string): One => api.post(`/montage/tasks/${id}/proposal/accept`, {}),
  rejectProposal: (id: string): One => api.post(`/montage/tasks/${id}/proposal/reject`, {}),
  submit: (id: string, itemId: string, body: { path: string; posterPath?: string; fileName: string; size: number; duration: number; comment?: string; images?: { path: string; name: string; size: number }[] }, opts?: RequestInit): One =>
    api.post(`/montage/tasks/${id}/items/${itemId}/versions`, body, opts),
  rework: (id: string, itemId: string, items: { tc: number; text: string; drawing?: FrameDrawing }[], opts?: RequestInit): One =>
    api.post(`/montage/tasks/${id}/items/${itemId}/rework`, { items }, opts),
  approve: (id: string, itemId: string, autoPublishInstagram?: boolean, opts?: RequestInit): One =>
    api.post(`/montage/tasks/${id}/items/${itemId}/approve`, { autoPublishInstagram }, opts),
  autoPublish: (id: string, itemId: string, enabled = true): One =>
    api.post(`/montage/tasks/${id}/items/${itemId}/auto-publish`, { enabled }),
  publishAfterModeration: (id: string, itemId: string, enabled: boolean): One =>
    api.post(`/montage/tasks/${id}/items/${itemId}/publish-after-moderation`, { enabled }),
  moderate: (id: string, itemId: string, body: { verdict: "ok" | "rework"; versionId?: string; items?: { tc: number; text: string; drawing?: FrameDrawing }[] }, opts?: RequestInit): One =>
    api.post(`/montage/tasks/${id}/items/${itemId}/moderation`, body, opts),
  /** Отменить своё решение модератора, пока его никто не подхватил (09.10.2026). */
  undoModeration: (id: string, itemId: string): One => api.post(`/montage/tasks/${id}/items/${itemId}/moderation/undo`, {}),
  /** Посты аккаунта ролика, вышедшие после принятия и ещё ни к чему не приложенные. */
  publishCandidates: (id: string, itemId: string, all = false) =>
    api.get<{ candidates: PostCandidate[]; syncedAt: string | null }>(`/montage/tasks/${id}/items/${itemId}/candidates${all ? "?all=1" : ""}`),
  /** «Мой ролик, темы нет»: тема задним числом, сразу выполненная, с этим постом. */
  claimPost: (mediaId: string, title: string): One => api.post("/montage/claim-post", { mediaId, title }),
  accountPosts: (accountId: string) =>
    api.get<{ posts: AccountPost[]; pending: WaitingItem[]; syncedAt: string | null }>("/montage/account-posts", { params: { account: accountId } }),
  /** «Не тот пост»: снять ссылку, которую приложила сверка обложек. */
  unlink: (id: string, itemId: string): One => api.post(`/montage/tasks/${id}/items/${itemId}/unlink`, {}),
  /** Ссылка на вышедший пост — без неё ролик не закрывается. */
  publishLink: (id: string, itemId: string, link: string, opts?: RequestInit): One =>
    api.post(`/montage/tasks/${id}/items/${itemId}/publish`, { link }, opts),
  cancelItem: (id: string, itemId: string, reason: string, opts?: RequestInit): One =>
    api.post(`/montage/tasks/${id}/items/${itemId}/cancel`, { reason }, opts),
  /** удалить несданное видео из темы совсем */
  removeItem: (id: string, itemId: string): One => api.delete(`/montage/tasks/${id}/items/${itemId}`),
  /** отозвать сданную версию, пока её никто не посмотрел */
  withdraw: (id: string, itemId: string): One => api.post(`/montage/tasks/${id}/items/${itemId}/withdraw`, {}),
  editItem: (id: string, itemId: string, body: { title?: string; brief?: string; accountId?: string | null }): One =>
    api.patch(`/montage/tasks/${id}/items/${itemId}`, body),
  editTask: (id: string, body: { title?: string; description?: string; workDate?: string; dueAt?: string }): One => api.patch(`/montage/tasks/${id}`, body),
  /** «Что успели — то успели»: день закрывается словами руководителя. */
  justify: (id: string, text: string, opts?: RequestInit): One =>
    api.post(`/montage/tasks/${id}/justify`, { text }, opts),
  schedule: (id: string, body: { divisionId: string; editorId: string | null; workDate: string; dueAt: string; estimate: number }, opts?: RequestInit): One =>
    api.post(`/montage/tasks/${id}/schedule`, body, opts),
  cancel: (id: string, opts?: RequestInit) => api.post<{ ok: true }>(`/montage/tasks/${id}/cancel`, {}, opts),
  restore: (id: string): One => api.post(`/montage/tasks/${id}/restore`, {}),
  remind: (id: string): One => api.post(`/montage/tasks/${id}/remind`, {}),
  toggleRevision: (revId: string, resolved: boolean): One => api.patch(`/montage/revisions/${revId}`, { resolved }),
  message: (id: string, text: string): One => api.post(`/montage/tasks/${id}/messages`, { text }),
  fileUrl: (fileId: string) => api.get<{ url: string; name: string }>(`/montage/files/${fileId}/url`),
  versionUrl: (versionId: string) => api.get<{ url: string; name: string }>(`/montage/versions/${versionId}/url`),

  /**
   * Скачать: ссылка подписана с именем файла, поэтому браузер сохраняет, а не
   * открывает. Несколько файлов — по очереди, с паузой: иначе браузер
   * посчитает пачку загрузок навязчивой и оставит одну.
   */
  async download(targets: { kind: "file" | "version"; id: string }[]) {
    for (const [i, t] of targets.entries()) {
      const { url } = t.kind === "file" ? await montageApi.fileUrl(t.id) : await montageApi.versionUrl(t.id);
      const a = document.createElement("a");
      a.href = url;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      if (i < targets.length - 1) await new Promise((r) => setTimeout(r, 400));
    }
  },

  /**
   * Файл — прямо в хранилище по подписанной ссылке, с прогрессом. Возвращает
   * путь, который потом называют серверу при постановке или сдаче версии.
   */
  async upload(
    purpose: "source" | "version" | "poster",
    fileOrBlob: File | Blob,
    opts: { taskId?: string; fileName?: string; onProgress?: (percent: number) => void; signal?: AbortSignal } = {},
  ): Promise<string> {
    const name = opts.fileName ?? (fileOrBlob instanceof File ? fileOrBlob.name : "file.bin");
    const ticket = await api.post<{ path: string; signedUrl: string; token: string; bucket: string; maxBytes?: number }>(
      "/montage/uploads",
      { purpose, fileName: name, taskId: opts.taskId },
    );
    // Сверяемся с пределом ДО заливки. Хранилище отвечает отказом только после
    // того, как файл уже уехал: на 57 мегабайтах монтажёр ждал пятнадцать
    // секунд, чтобы получить «The object exceeded the maximum allowed size».
    if (ticket.maxBytes && fileOrBlob.size > ticket.maxBytes) {
      throw new Error(
        `${mb(fileOrBlob.size)} — больше предела в ${mb(ticket.maxBytes)}. Сожмите ролик или отдайте ссылкой`,
      );
    }
    const asFile = fileOrBlob instanceof File ? fileOrBlob : new File([fileOrBlob], name, { type: fileOrBlob.type });
    await uploadToSignedUrl({ ticket: ticket as any, file: asFile, onProgress: opts.onProgress, signal: opts.signal });
    return ticket.path;
  },
};

/** «57 МБ» — так же, как размер подписан в строке загрузки. */
const mb = (bytes: number) => `${Math.round(bytes / (1024 * 1024))} МБ`;

export { readVideoDuration };
